import { GpmPayConfigError, GpmPayWebhookSignatureError } from '../core/errors.js';
import {
  SIGNATURE_HEADER,
  constructWebhookEvent,
  type GpmPayWebhookEvent,
} from './verify.js';

/**
 * Structurally-typed request. Deliberately not `express.Request` — this package
 * has zero dependencies and must not force Express into your tree.
 */
export interface MinimalRequest {
  headers: Record<string, string | string[] | undefined>;
  body?: unknown;
  rawBody?: unknown;
}

export interface MinimalResponse {
  status(code: number): MinimalResponse;
  json(body: unknown): unknown;
  send(body?: unknown): unknown;
}

export type NextFunction = (error?: unknown) => void;

export interface GpmpayWebhookOptions {
  /** The webhook setting's `authorizationSecret`. */
  secret: string;
  onEvent: (
    event: GpmPayWebhookEvent,
    req: MinimalRequest,
  ) => void | Promise<void>;
  toleranceSeconds?: number;
  /** Defaults to `X-GPMPay-Signature`. */
  headerName?: string;
  onError?: (error: GpmPayWebhookSignatureError, req: MinimalRequest) => void;
  /**
   * Respond 200 before awaiting `onEvent`. Default true.
   *
   * GPM Pay aborts a delivery after 5s and retries on the schedule
   * `[10s, 30s, 2m, 10m, 1h, 6h]`, so a slow handler causes duplicate
   * processing. Acknowledge fast, work afterwards.
   */
  respondEarly?: boolean;
}

const BODY_PARSER_HINT =
  'The GPM Pay webhook handler received an already-parsed body.\n' +
  'HMAC verification runs over the exact bytes GPM Pay sent, and re-serializing a\n' +
  'parsed object changes them — verification will ALWAYS fail.\n\n' +
  'Mount the raw body parser on this route only:\n' +
  "  app.post('/webhooks/gpmpay',\n" +
  "    express.raw({ type: 'application/json' }),\n" +
  '    gpmpayWebhook({ secret, onEvent }));\n\n' +
  'If a global express.json() already ran, capture the raw body with its verify hook:\n' +
  '  app.use(express.json({ verify: (req, _res, buf) => { req.rawBody = buf; } }));';

function resolveRawBody(req: MinimalRequest): Buffer | string {
  if (Buffer.isBuffer(req.rawBody)) return req.rawBody;
  if (typeof req.rawBody === 'string') return req.rawBody;
  if (Buffer.isBuffer(req.body)) return req.body;
  if (typeof req.body === 'string') return req.body;

  throw new GpmPayConfigError(BODY_PARSER_HINT);
}

/**
 * Express-compatible middleware that verifies the signature and hands you a
 * typed event.
 *
 * @example
 * app.post(
 *   '/webhooks/gpmpay',
 *   express.raw({ type: 'application/json' }),
 *   gpmpayWebhook({
 *     secret: process.env.GPMPAY_WEBHOOK_SECRET!,
 *     onEvent: async (event) => {
 *       if (event.payload.transferType !== 'in') return;
 *       const code = /DH(\d+)/.exec(event.payload.content)?.[0];
 *       if (code) await fulfil(code, event.payload.transferAmount);
 *     },
 *   }),
 * );
 */
export function gpmpayWebhook(options: GpmpayWebhookOptions) {
  const {
    secret,
    onEvent,
    toleranceSeconds,
    headerName = SIGNATURE_HEADER,
    onError,
    respondEarly = true,
  } = options;

  return async function handler(
    req: MinimalRequest,
    res: MinimalResponse,
    next: NextFunction,
  ): Promise<void> {
    let event: GpmPayWebhookEvent;

    try {
      const rawBody = resolveRawBody(req);
      const headerKey = Object.keys(req.headers).find(
        (key) => key.toLowerCase() === headerName.toLowerCase(),
      );
      const rawHeader = headerKey === undefined ? undefined : req.headers[headerKey];
      const signature = Array.isArray(rawHeader) ? rawHeader[0] : rawHeader;

      event = constructWebhookEvent({
        rawBody,
        signature: signature ?? '',
        secret,
        headers: req.headers,
        ...(toleranceSeconds === undefined ? {} : { toleranceSeconds }),
      });
    } catch (error) {
      if (error instanceof GpmPayWebhookSignatureError) {
        onError?.(error, req);
        res.status(401).json({ error: error.message, reason: error.reason });
        return;
      }
      next(error);
      return;
    }

    if (respondEarly) {
      res.status(200).json({ received: true });
      try {
        await onEvent(event, req);
      } catch (error) {
        next(error);
      }
      return;
    }

    try {
      await onEvent(event, req);
      res.status(200).json({ received: true });
    } catch (error) {
      next(error);
    }
  };
}
